import { 
  FileText, 
  Image, 
  Type, 
  Calculator, 
  Lock, 
  StickyNote,
  Merge,
  Split,
  Minimize2,
  ImageIcon,
  Shrink,
  Scaling,
  RefreshCw,
  CaseSensitive,
  Hash,
  Eraser,
  Volume2,
  Wallet,
  Receipt,
  Percent,
  Calendar,
  KeyRound,
  QrCode,
  Fingerprint,
  Binary,
  NotebookPen,
  ListTodo,
  Clipboard,
  LucideIcon
} from "lucide-react";

export type CategoryId = 'pdf' | 'image' | 'text' | 'calculator' | 'utility' | 'mini';

export interface Tool {
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  category: CategoryId;
  route: string;
}

export interface Category {
  id: CategoryId;
  name: string;
  icon: LucideIcon;
  description: string;
}

export const categories: Category[] = [
  { id: 'pdf', name: 'PDF Tools', icon: FileText, description: 'Merge, split, compress PDFs' },
  { id: 'image', name: 'Image Tools', icon: Image, description: 'Compress, resize, convert images' },
  { id: 'text', name: 'Text Tools', icon: Type, description: 'Text manipulation utilities' },
  { id: 'calculator', name: 'Calculators', icon: Calculator, description: 'Financial & utility calculators' },
  { id: 'utility', name: 'Utility Tools', icon: Lock, description: 'Passwords, QR codes & more' },
  { id: 'mini', name: 'Mini Tools', icon: StickyNote, description: 'Notes, todos & quick tools' },
];

export const tools: Tool[] = [
  // PDF Tools
  { id: 'pdf-merge', name: 'PDF Merge', description: 'Combine multiple PDF files into one', icon: Merge, category: 'pdf', route: '/tools/pdf-merge' },
  { id: 'pdf-split', name: 'PDF Split', description: 'Split PDF into multiple files', icon: Split, category: 'pdf', route: '/tools/pdf-split' },
  { id: 'pdf-compress', name: 'PDF Compress', description: 'Reduce PDF file size', icon: Minimize2, category: 'pdf', route: '/tools/pdf-compress' },
  { id: 'pdf-to-image', name: 'PDF to Image', description: 'Convert PDF pages to images', icon: ImageIcon, category: 'pdf', route: '/tools/pdf-to-image' },

  // Image Tools
  { id: 'image-compress', name: 'Image Compressor', description: 'Reduce image file size', icon: Shrink, category: 'image', route: '/tools/image-compress' },
  { id: 'image-resize', name: 'Image Resizer', description: 'Resize images to any dimension', icon: Scaling, category: 'image', route: '/tools/image-resize' },
  { id: 'image-convert', name: 'Image Converter', description: 'Convert between JPG, PNG, WebP', icon: RefreshCw, category: 'image', route: '/tools/image-convert' },

  // Text Tools
  { id: 'text-case', name: 'Text Case Converter', description: 'Change text case easily', icon: CaseSensitive, category: 'text', route: '/tools/text-case' },
  { id: 'word-counter', name: 'Word Counter', description: 'Count words and characters', icon: Hash, category: 'text', route: '/tools/word-counter' },
  { id: 'text-clean', name: 'Remove Extra Spaces', description: 'Clean up messy text', icon: Eraser, category: 'text', route: '/tools/text-clean' },
  { id: 'text-to-speech', name: 'Text to Speech', description: 'Convert text to audio', icon: Volume2, category: 'text', route: '/tools/text-to-speech' },

  // Calculators
  { id: 'emi-calculator', name: 'EMI Calculator', description: 'Calculate loan EMI', icon: Wallet, category: 'calculator', route: '/tools/emi-calculator' },
  { id: 'gst-calculator', name: 'GST Calculator', description: 'Calculate GST amounts', icon: Receipt, category: 'calculator', route: '/tools/gst-calculator' },
  { id: 'percentage-calculator', name: 'Percentage Calculator', description: 'Calculate percentages', icon: Percent, category: 'calculator', route: '/tools/percentage-calculator' },
  { id: 'age-calculator', name: 'Age Calculator', description: 'Calculate exact age', icon: Calendar, category: 'calculator', route: '/tools/age-calculator' },

  // Utility Tools
  { id: 'password-generator', name: 'Password Generator', description: 'Generate secure passwords', icon: KeyRound, category: 'utility', route: '/tools/password-generator' },
  { id: 'qr-generator', name: 'QR Code Generator', description: 'Create QR codes instantly', icon: QrCode, category: 'utility', route: '/tools/qr-generator' },
  { id: 'uuid-generator', name: 'UUID Generator', description: 'Generate unique identifiers', icon: Fingerprint, category: 'utility', route: '/tools/uuid-generator' },
  { id: 'base64', name: 'Base64 Encode/Decode', description: 'Encode or decode Base64', icon: Binary, category: 'utility', route: '/tools/base64' },

  // Mini Tools
  { id: 'notes', name: 'Notes Saver', description: 'Save notes locally', icon: NotebookPen, category: 'mini', route: '/tools/notes' },
  { id: 'todo', name: 'To-Do List', description: 'Simple task manager', icon: ListTodo, category: 'mini', route: '/tools/todo' },
  { id: 'clipboard', name: 'Clipboard History', description: 'Track clipboard history', icon: Clipboard, category: 'mini', route: '/tools/clipboard' },
];

export const getToolsByCategory = (categoryId: CategoryId): Tool[] => {
  return tools.filter(tool => tool.category === categoryId);
};

export const getCategoryById = (categoryId: CategoryId): Category | undefined => {
  return categories.find(cat => cat.id === categoryId);
};

export const searchTools = (query: string): Tool[] => {
  const lowerQuery = query.toLowerCase();
  return tools.filter(tool => 
    tool.name.toLowerCase().includes(lowerQuery) ||
    tool.description.toLowerCase().includes(lowerQuery)
  );
};
